import { faTicket } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BrandS, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ANY_SPORT_MARKET_WHEEL_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useEnabledWheelByRarity } from '@sorare/core/src/hooks/useEnabledWheelByRarity';

import wheel from 'assets/wheel/wheel.svg';
import { useWheelTickets } from 'hooks/wheel/useWheelTickets';

import { HowToWinTicketsButton } from '../HowToWinTicketsButton';
import background from './assets/background.webp';

const Banner = styled(Vertical)`
  margin: 0 calc(var(--container-padding) * -1);
  padding: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-150);
  backdrop-filter: blur(var(--triple-unit));
  background:
    url(${background}) center / cover no-repeat,
    var(--c-nd-50);
`;
const Left = styled(Horizontal)`
  max-width: 50%;
`;
const Wheel = styled.img.attrs({ src: wheel })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
`;
const StyledLabelS = styled(LabelS)`
  display: inline-flex;
  gap: var(--half-unit);
  align-items: center;
`;

export const WheelBanner = ({ rarity }: { rarity: Rarity }) => {
  const { currentUser } = useCurrentUserContext();
  const enabledWheelByRarity = useEnabledWheelByRarity();
  const { ticketCount, canClaimTicket } = useWheelTickets({
    excludeCommonTickets: true,
  });
  const { generateSportContextPath } = useSportContext();
  if (!currentUser || !enabledWheelByRarity.limited) return null;
  return (
    <LinkBox>
      <Banner>
        <SBHorizontal gap={1}>
          <Left as="span">
            <Wheel />
            <BrandS>
              <FormattedMessage
                id="wheel.banner.title"
                defaultMessage="Sorare Wheel is open!"
              />
            </BrandS>
          </Left>
          <Horizontal>
            <HowToWinTicketsButton color="tertiary" size="small" />
            <LinkOverlay
              size="small"
              color="quaternary"
              as={Button}
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                params: {
                  rarity,
                },
              })}
              endIcon={
                (!!canClaimTicket || ticketCount > 0) && (
                  <Chip size="compact" color="red">
                    <StyledLabelS bold>
                      <FontAwesomeIcon icon={faTicket} />
                      {!!ticketCount && <FormattedNumber value={ticketCount} />}
                    </StyledLabelS>
                  </Chip>
                )
              }
            >
              <FormattedMessage
                id="wheel.banner.enter"
                defaultMessage="Enter"
              />
            </LinkOverlay>
          </Horizontal>
        </SBHorizontal>
      </Banner>
    </LinkBox>
  );
};
